import { format, isSameYear } from "date-fns";

import { AccountTableView } from "@bitwarden/web-vault/app/models/types/account.types";

/** Which colour the share meter and the status dot take. */
export type AccountStatusLevel = "ok" | "warn" | "err" | "neutral";

export interface AccountStatusLine {
  level: AccountStatusLevel;
  text: string;
  /** A fetch is running: the line shows the (spinning) sync icon instead of a dot. */
  syncing: boolean;
  /** Sprite id of the sync icon, when `syncing`. */
  icon?: string;
  spin?: boolean;
}

/** How the card's meter reads: an asset's slice of all assets, or a liability against them. */
export type AccountMeterTone = "asset" | "credit" | "debt";

export interface AccountListItemView {
  id: string;
  /** The row the account icon renders from. */
  row: AccountTableView;
  name: string;
  subtitle: string;
  /** "Fiskil", "Manual", … — the account's source. */
  tag: string;
  /** Shown as-is (compact), already signed for display; a liability is shown as its magnitude. */
  balance: number;
  amountTone: "positive" | "negative" | "zero";
  /** "AUD", or "AUD owed" for a liability. */
  unit: string;
  isLiability: boolean;
  meter: { percent: number; tone: AccountMeterTone; label: string };
  status: AccountStatusLine;
}

/** An asset account's share of everything the user holds, 0-100. */
export function assetSharePercent(balance: number, totalAssets: number): number {
  if (!(balance > 0) || !(totalAssets > 0)) {
    return 0;
  }
  return Math.min(100, Math.round((balance / totalAssets) * 100));
}

/**
 * A liability measured against the user's assets, as a whole percentage (it can exceed 100).
 * With no assets at all any debt is maximally leveraged, not 0%.
 */
export function leveragePercent(owed: number, totalAssets: number): number {
  const magnitude = Math.abs(owed);
  if (!(magnitude > 0)) {
    return 0;
  }
  if (!(totalAssets > 0)) {
    return 100;
  }
  return Math.round((magnitude / totalAssets) * 100);
}

/** "13 Oct", or "13 Oct 2025" when it is not this year. */
export function shortDate(date: Date, now: Date = new Date()): string {
  return format(date, isSameYear(date, now) ? "d MMM" : "d MMM yyyy");
}

/**
 * Whether an account is picked by the list's search box: every word typed has to appear somewhere
 * in the account's name, subtitle or source. Case-insensitive; an empty search matches everything.
 */
export function matchesAccountSearch(
  view: Pick<AccountListItemView, "name" | "subtitle" | "tag">,
  term: string,
): boolean {
  const words = term.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return true;
  }
  const haystack = `${view.name} ${view.subtitle} ${view.tag}`.toLowerCase();
  return words.every((word) => haystack.includes(word));
}

const SIMPLE_LIST_KEY = "gloss.accounts.simpleList";

/** The remembered "Simple list" choice; off when nothing is stored or storage is unavailable. */
export function readSimpleListPreference(): boolean {
  try {
    return localStorage.getItem(SIMPLE_LIST_KEY) === "1";
  } catch {
    return false;
  }
}

export function saveSimpleListPreference(simple: boolean): void {
  try {
    localStorage.setItem(SIMPLE_LIST_KEY, simple ? "1" : "0");
  } catch {
    // Private mode / blocked storage: the choice just won't be remembered.
  }
}

// ── Colours shared by the list card and the details hero ─────────────────────────────────────
// One class string per state (never a conditional colour next to a static one) so the same
// account reads the same in both places.

export function amountToneClass(tone: AccountListItemView["amountTone"]): string {
  switch (tone) {
    case "positive":
      return "tw-text-color-money-in-text";
    case "negative":
      return "tw-text-color-status-error-text";
    default:
      return "tw-text-color-text-secondary";
  }
}

/** The filled part of the share meter. */
export function meterToneClass(tone: AccountMeterTone): string {
  switch (tone) {
    case "credit":
      return "tw-bg-dt-warning-500";
    case "debt":
      return "tw-bg-dt-error-500";
    default:
      return "tw-bg-dt-custom-teal-11";
  }
}

/** The softer fill behind the share figure in the details hero. */
export function shareBarToneClass(tone: AccountMeterTone): string {
  switch (tone) {
    case "credit":
      return "tw-bg-dt-warning-500/25";
    case "debt":
      return "tw-bg-dt-error-500/20";
    default:
      return "tw-bg-dt-custom-teal-4 dark:tw-bg-dt-custom-teal-9/30";
  }
}

export function statusDotClass(level: AccountStatusLevel): string {
  switch (level) {
    case "ok":
      return "tw-bg-dt-custom-teal-9";
    case "warn":
      return "tw-bg-dt-warning-500";
    case "err":
      return "tw-bg-dt-error-500";
    default:
      return "tw-bg-dt-text-tertiary";
  }
}

export function statusTextClass(level: AccountStatusLevel): string {
  switch (level) {
    case "warn":
      return "tw-text-dt-warning-500";
    case "err":
      return "tw-text-dt-text-error";
    default:
      return "tw-text-dt-text-secondary";
  }
}
